// src/pages/PropertiesScreen.js

import React, { useState, useEffect, useMemo } from 'react';
import { PlusCircle, Edit, Search, DollarSign, Download, MoreVertical, UserPlus, Settings, LogIn } from 'lucide-react'; 
import { api } from '../api';
import toast from 'react-hot-toast';

// --- Style and Component Imports ---
import '../styles/PropertiesScreen.css';
import PropertyModal from '../components/properties/PropertyModal';
import AddServiceModal from '../components/properties/AddServiceModal';
import EditEmployeeModal from '../components/employees/EditEmployeeModal';
import AddEmployeeModal from '../components/employees/AddEmployeeModal';
import InviteToPortalModal from '../components/employees/InviteToPortalModal';
import DeviceModal from '../components/devices/DeviceModal';
import RebootModal from '../components/devices/RebootModal';
import WiFiConfigModal from '../components/devices/WiFiConfigModal';
import SetTimeModal from '../components/devices/SetTimeModal';
import AccessTimeModal from '../components/devices/AccessTimeModal';
import NtpSettingsModal from '../components/devices/NtpSettingsModal';
import PropertySettingsTab from '../components/properties/PropertySettingsTab'; 
import PropertyRoomsTab from '../components/rooms/PropertyRoomsTab';
import PropertyPermissionsTab from '../components/properties/PropertyPermissionsTab';

// --- Helper Component for Workforce (FIXED PERMISSIONS) ---
const RoleSection = ({ title, people, onPersonClick, currentUser, onImpersonate, dimmed, onInviteToPortal }) => {
    if (!people || people.length === 0) {
        return <div className="role-section empty"><h3 className="role-header">{title}</h3><p>No one assigned to this role.</p></div>;
    }

    // Helper: Only SuperAdmins can impersonate, but not other SuperAdmins
    const canImpersonate = (targetRole) => {
        return currentUser && currentUser.role === 'SuperAdmin' && targetRole !== 'SuperAdmin';
    };

    // --- FIX: HIERARCHY BASED CHECK ---
    const canUpdate = (targetRole) => {
        if (!currentUser || !currentUser.role) return false;
        
        // 1. SuperAdmin can edit everyone
        if (currentUser.role === 'SuperAdmin') return true;

        // 2. Define Ranks
        const roleValues = {
            'SuperAdmin': 100,
            'Owner': 50,
            'Manager': 40,
            'Accountant': 30,
            'Housekeeper': 10
        };

        const myLevel = roleValues[currentUser.role] || 0;
        const targetLevel = roleValues[targetRole] || 0;

        // 3. Allow if My Rank >= Target Rank
        // Using >= allows Managers to edit other Managers (Peers), 
        // but the Modal will restrict changing the Role itself.
        return myLevel >= targetLevel; 
    };

    return (
        <div className={`role-section ${dimmed ? 'dimmed-section' : ''}`}>
            <h3 className="role-header">{title}</h3>
            <div className="people-grid">
                {people.map(person => {
                    const isClickable = canUpdate(person.role);
                    const showImpersonate = canImpersonate(person.role);

                    return (
                        <div
                            key={`${title}-${person.id}`}
                            className={`person-card ${isClickable ? '' : 'disabled'}`}
                            // Only trigger Edit on main card click if NOT clicking a specific action button
                            onClick={(e) => {
                                if (e.target.closest('.impersonate-btn')) return;
                                if (isClickable) onPersonClick(person);
                            }}
                            style={{ position: 'relative' }} 
                        >
                            {/* --- IMPERSONATE BUTTON (Top Right) --- */}
                            {showImpersonate && (
                                <button
                                    className="impersonate-btn"
                                    onClick={(e) => {
                                        e.stopPropagation(); 
                                        onImpersonate(person);
                                    }}
                                    title={`Log in as ${person.first_name}`}
                                    style={{
                                        position: 'absolute', top: '8px', right: '8px',
                                        background: 'transparent', border: 'none', cursor: 'pointer',
                                        color: '#64748b', padding: '4px', borderRadius: '4px',
                                        transition: 'background 0.2s', zIndex: 10
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
                                >
                                    <LogIn size={16} />
                                </button>
                            )}

                            {/* --- INVITE TO PORTAL BUTTON (Bottom Right) --- */}
                            {person.role === 'Housekeeper' && person.is_active && onInviteToPortal && (
                                <button
                                    className="invite-portal-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onInviteToPortal(person);
                                    }}
                                    title={`Invite ${person.first_name} to Portal`}
                                    style={{
                                        position: 'absolute', bottom: '8px', right: '8px',
                                        background: 'transparent', border: '1px solid #e2e8f0', cursor: 'pointer',
                                        color: '#6366f1', padding: '4px 8px', borderRadius: '4px',
                                        fontSize: '0.7rem', fontWeight: 500,
                                        transition: 'all 0.2s', zIndex: 10
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = '#6366f1'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#6366f1'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6366f1'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                                >
                                    Invite
                                </button>
                            )}

                            <div className="person-avatar">{person.first_name.charAt(0)}{person.last_name.charAt(0)}</div>
                            <div className="person-details">
                                <div className="person-name">{person.first_name} {person.last_name}</div>
                                <div className="person-email">{person.email}</div>
                            </div>
                            <span className={`status-badge-dot ${person.is_active ? 'active' : 'inactive'}`}></span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// --- Main Properties Screen Component ---
const PropertiesScreen = ({ user }) => {
    const [properties, setProperties] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProperty, setSelectedProperty] = useState(null);
    
    // Default Tab & Sort
    const [activeTab, setActiveTab] = useState('rooms'); 
    const [sortOrder, setSortOrder] = useState('last_updated');
    
    const [workforceData, setWorkforceData] = useState({ Owner: [], Manager: [], Accountant: [], Housekeeper: [] });
    const [isWorkforceLoading, setIsWorkforceLoading] = useState(false);
    const [housekeeperFilter, setHousekeeperFilter] = useState('active');
    const [devices, setDevices] = useState([]);
    const [isDevicesLoading, setIsDevicesLoading] = useState(false);
    const [propertyHasActiveSubscriptions, setPropertyHasActiveSubscriptions] = useState(false);
    
    // Modals & Menus
    const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
    const [propertyToEdit, setPropertyToEdit] = useState(null);
    const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
    const [isEditEmployeeModalOpen, setIsEditEmployeeModalOpen] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
    const [deviceToEdit, setDeviceToEdit] = useState(null);
    const [serviceModalState, setServiceModalState] = useState({ isOpen: false, property: null });
    const [openActionMenu, setOpenActionMenu] = useState(null);
    const [openDeviceActionMenu, setOpenDeviceActionMenu] = useState(null);
    const [deviceActionState, setDeviceActionState] = useState({ type: null, device: null });
    const [isSubmittingDeviceAction, setIsSubmittingDeviceAction] = useState(false);

    // Impersonation Modal State
    const [impersonateTarget, setImpersonateTarget] = useState(null);

    // Invite to Portal Modal State
    const [isInvitePortalModalOpen, setIsInvitePortalModalOpen] = useState(false);
    const [invitePortalPerson, setInvitePortalPerson] = useState(null);

    const canManageProperties = user && user.role === 'SuperAdmin';
    const canDownload = user && ['SuperAdmin', 'Owner', 'Manager'].includes(user.role);
    const canViewActions = canManageProperties || canDownload;
    const canAddEmployee = user && ['SuperAdmin', 'Owner', 'Manager'].includes(user.role);
    const canManageSettings = user && ['SuperAdmin', 'Owner', 'Manager'].includes(user.role);
    const canManagePermissions = user && ['SuperAdmin', 'Owner'].includes(user.role);
    const canManageRooms = user && ['SuperAdmin', 'Owner', 'Manager'].includes(user.role);

    const fetchProperties = async () => {
        setIsLoading(true);
        try {
            const propertiesData = await api.getProperties();
            setProperties(propertiesData || []);
            
            if (selectedProperty) {
                 const updatedSelected = propertiesData.find(p => p.id === selectedProperty.id);
                 if (updatedSelected) setSelectedProperty(updatedSelected);
            } else if (propertiesData && propertiesData.length > 0) {
                const sortedInitial = [...(propertiesData || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                setSelectedProperty(sortedInitial[0]);
            }
        } catch (err) { setError(err.message); }
        finally { setIsLoading(false); }
    };

    useEffect(() => {
        if (user) fetchProperties();
    }, [user]);

    useEffect(() => {
        if (!selectedProperty) return;
        const loadTabData = async () => {
            if (activeTab === 'workforce') {
                setIsWorkforceLoading(true);
                try {
                    const flatList = await api.getWorkforce({ propertyId: selectedProperty.id });
                    const data = (flatList || []).reduce((acc, person) => {
                        const groupRole = person.role === 'SuperAdmin' ? 'Owner' : person.role;
                        if (!acc[groupRole]) acc[groupRole] = [];
                        acc[groupRole].push(person);
                        return acc;
                    }, { Owner: [], Manager: [], Accountant: [], Housekeeper: [] });
                    setWorkforceData(data);
                } catch (error) { toast.error(`Failed to load workforce: ${error.message}`); }
                finally { setIsWorkforceLoading(false); }
            } else if (activeTab === 'devices') {
                setIsDevicesLoading(true);
                try {
                    const devicesData = await api.getDevices({ propertyId: selectedProperty.id });
                    setDevices(devicesData || []);
                } catch (error) { toast.error(`Failed to load devices: ${error.message}`); }
                finally { setIsDevicesLoading(false); }
            }
        };
        loadTabData();
    }, [selectedProperty, activeTab]);

    // Always fetch device count for the selected property (for Quick Stats)
    useEffect(() => {
        if (!selectedProperty) return;
        const fetchDeviceCount = async () => {
            try {
                const devicesData = await api.getDevices({ propertyId: selectedProperty.id });
                setDevices(devicesData || []);
            } catch (error) { /* silent fail for count */ }
        };
        fetchDeviceCount();
    }, [selectedProperty]);

    // Always fetch workforce count for the selected property (for Quick Stats)
    useEffect(() => {
        if (!selectedProperty) return;
        const fetchWorkforceCount = async () => {
            try {
                const flatList = await api.getWorkforce({ propertyId: selectedProperty.id });
                const data = (flatList || []).reduce((acc, person) => {
                    const groupRole = person.role === 'SuperAdmin' ? 'Owner' : person.role;
                    if (!acc[groupRole]) acc[groupRole] = [];
                    acc[groupRole].push(person);
                    return acc;
                }, { Owner: [], Manager: [], Accountant: [], Housekeeper: [] });
                setWorkforceData(data);
            } catch (error) { /* silent fail for count */ }
        };
        fetchWorkforceCount();
    }, [selectedProperty]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openActionMenu && !event.target.closest('.action-menu-container')) setOpenActionMenu(null);
            if (openDeviceActionMenu && !event.target.closest('.action-menu-container')) setOpenDeviceActionMenu(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openActionMenu, openDeviceActionMenu]);

    // Check if selected property has active subscriptions
    useEffect(() => {
        if (!selectedProperty) {
            setPropertyHasActiveSubscriptions(false);
            return;
        }
        const checkSubscriptions = async () => {
            try {
                const subs = await api.getPropertySubscriptions(selectedProperty.id);
                setPropertyHasActiveSubscriptions(subs && subs.length > 0);
            } catch (error) {
                setPropertyHasActiveSubscriptions(false);
            }
        };
        checkSubscriptions();
    }, [selectedProperty]);

    const handleSelectProperty = (property) => { 
        setSelectedProperty(property); 
        setActiveTab('rooms'); 
        setOpenActionMenu(null);
        // Store selected property ID in localStorage for Sidebar permissions
        localStorage.setItem('selectedPropertyId', property.id);
    };

    const handleDownloadAll = (propertyCode) => { toast.promise(api.downloadAllImageFiles(propertyCode), { loading: 'Preparing download...', success: 'Download started!', error: (err) => `Download failed: ${err.message}`, }); };
    const handleOpenAddPropertyModal = () => { setPropertyToEdit(null); setError(null); setIsPropertyModalOpen(true); };
    const handleOpenEditPropertyModal = (property) => { setPropertyToEdit(property); setError(null); setIsPropertyModalOpen(true); };
    const handleOpenServiceModal = (property) => { setServiceModalState({ isOpen: true, property: property }); };

    const handleSaveProperty = async (propertyData) => {
        const savePromise = propertyToEdit ? api.updateProperty(propertyToEdit.id, propertyData) : api.createProperty(propertyData);
        toast.promise(savePromise, {
            loading: 'Saving property...',
            success: () => { setIsPropertyModalOpen(false); setPropertyToEdit(null); fetchProperties(); return 'Property saved successfully!'; },
            error: (err) => `Error: ${err.message}`,
        });
    };

    const handleAddService = async (serviceData) => {
        try {
            await api.createSubscription(serviceData);
            toast.success('Service added successfully! Invoice created.');
        } catch (error) {
            toast.error(error.message || 'Failed to add service.');
        }
    };
    
    const handleOpenAddEmployeeModal = () => setIsAddEmployeeModalOpen(true);
    const handleOpenEditEmployeeModal = (person) => { setSelectedPerson(person); setIsEditEmployeeModalOpen(true); };
    const refreshWorkforce = () => { setActiveTab(''); setTimeout(() => setActiveTab('workforce'), 10); };
    const handleOpenAddDeviceModal = () => { setDeviceToEdit(null); setIsDeviceModalOpen(true); };
    const handleOpenEditDeviceModal = (device) => { setDeviceToEdit(device); setIsDeviceModalOpen(true); };
    const refreshDevices = () => { setActiveTab(''); setTimeout(() => setActiveTab('devices'), 10); };

    // Impersonation Handlers
    const handleInitiateImpersonation = (targetUser) => { setImpersonateTarget(targetUser); };
    const executeImpersonation = async () => {
        if (!impersonateTarget) return;
        const toastId = toast.loading(`Switching to ${impersonateTarget.first_name}...`);
        try {
            const data = await api.impersonateUser(impersonateTarget.id);
            const currentToken = localStorage.getItem('authToken');
            const currentRefreshToken = localStorage.getItem('refreshToken');
            if (currentToken) localStorage.setItem('adminAuthToken', currentToken);
            if (currentRefreshToken) localStorage.setItem('adminRefreshToken', currentRefreshToken);
            localStorage.setItem('authToken', data.accessToken);
            localStorage.setItem('refreshToken', data.refreshToken);
            toast.success(`Welcome, ${impersonateTarget.first_name}!`, { id: toastId });
            setTimeout(() => { window.location.href = '/'; }, 800);
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Failed to impersonate", { id: toastId });
            setImpersonateTarget(null);
        }
    };

    const handleSaveDevice = async (deviceData) => {
        const savePromise = deviceToEdit ? api.updateDevice(deviceToEdit.id, deviceData) : api.createDevice(deviceData);
        toast.promise(savePromise, {
            loading: 'Saving device...',
            success: () => { setIsDeviceModalOpen(false); setDeviceToEdit(null); refreshDevices(); return `Device ${deviceToEdit ? 'updated' : 'created'} successfully!`; },
            error: (err) => `Error: ${err.message}`,
        });
    };

    const handleEmployeeCreationComplete = (newEmployeeData) => {
        refreshWorkforce();
        closeDeviceActionModal();
        const newPerson = {
            id: newEmployeeData.userId,
            first_name: newEmployeeData.firstName,
            last_name: newEmployeeData.lastName,
            email: '',
            phone: '',
            role: 'Housekeeper',
            is_active: true,
            employee_code: newEmployeeData.employeeCode
        };
        setSelectedPerson(newPerson);
        setIsEditEmployeeModalOpen(true);
    };
    
    const handleDeviceMenuAction = (type, device) => { setDeviceActionState({ type, device }); setOpenDeviceActionMenu(null); };
    const closeDeviceActionModal = () => setDeviceActionState({ type: null, device: null });
    const handleDeviceAction = async (actionType, deviceId, data = {}) => {
        setIsSubmittingDeviceAction(true);
        const actionPromise = api[actionType](deviceId, data);
        toast.promise(actionPromise, {
            loading: 'Sending command...',
            success: (response) => { closeDeviceActionModal(); return response.message || 'Action completed!'; },
            error: (err) => `Error: ${err.message}`,
        });
        try { await actionPromise; } catch (error) { /* Handled by toast */ }
        finally { setIsSubmittingDeviceAction(false); }
    };

    const sortedAndFilteredProperties = useMemo(() => {
        if (!properties) return [];
        const filtered = properties.filter(prop => (prop.name && prop.name.toLowerCase().includes(searchTerm.toLowerCase())) || (prop.code && prop.code.toLowerCase().includes(searchTerm.toLowerCase())));
        const sortable = [...filtered];
        sortable.sort((a, b) => {
            switch (sortOrder) {
                case 'last_created': return new Date(b.created_at) - new Date(a.created_at);
                case 'last_updated': const dateA = a.updated_at || a.created_at; const dateB = b.updated_at || b.created_at; return new Date(dateB) - new Date(dateA);
                case 'status': if (a.is_active === b.is_active) return a.name.localeCompare(b.name); return b.is_active - a.is_active;
                default: return a.name.localeCompare(b.name);
            }
        });
        return sortable;
    }, [searchTerm, properties, sortOrder]);

    const filteredHousekeepers = useMemo(() => {
        const all = workforceData.Housekeeper || [];
        if (housekeeperFilter === 'active') return all.filter(p => p.is_active);
        if (housekeeperFilter === 'inactive') return all.filter(p => !p.is_active);
        return all;
    }, [workforceData.Housekeeper, housekeeperFilter]);

    const formatAddress = (prop) => {
        if (!prop) return '';
        return [prop.address, prop.city, prop.state, prop.zip_code].filter(part => part).join(', ');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US');
    };

    if (isLoading) return <div className="page-container"><p>Loading properties...</p></div>;
    if (error && !isPropertyModalOpen) return <div className="page-container"><div className="error-message">{error}</div></div>;

    return (
        <div className="page-container">
            <div className="page-header"><h1 className="page-title">Properties</h1></div>
            <div className={`master-detail-layout ${selectedProperty ? 'detail-view-active' : ''}`}>
                <div className="master-column">
                    <div className="master-column-header">
                        <div className="search-input-container">
                            <Search size={18} className="search-icon" />
                            <input type="text" placeholder="Search properties..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                        {canManageProperties && (<button onClick={handleOpenAddPropertyModal} className="action-button"><PlusCircle size={18} /><span>Add Property</span></button>)}
                    </div>
                    <div className="sort-container">
                        <label htmlFor="property-sort" className="sort-label">Sort by</label>
                        <select id="property-sort" className="sort-select" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                            <option value="last_created">Last Created</option>
                            <option value="last_updated">Last Updated</option>
                            <option value="status">Active Status</option>
                            <option value="name_asc">Name (A-Z)</option>
                        </select>
                    </div>
                    <div className="property-list">
                        {(sortedAndFilteredProperties || []).map(prop => (
                            <div key={prop.id} className={`property-list-item ${selectedProperty?.id === prop.id ? 'selected' : ''}`} onClick={() => handleSelectProperty(prop)}>
                                <div className="property-list-item-header">
                                    <span className={`property-status-dot ${prop.is_active ? 'active' : 'inactive'}`}></span>
                                    <span className="property-list-item-name">{prop.name}</span>
                                </div>
                                <span className="property-list-item-code">{prop.code}</span>
                                {prop.product_names && (
                                    <div className="property-list-item-products">
                                        {prop.product_names.split(', ').map((product, idx) => {
                                            const productColors = {
                                                'Time Tracking Service': { bg: '#dcfce7', color: '#16a34a' },
                                                'Domain Registration': { bg: '#f3e8ff', color: '#7c3aed' },
                                                'Website Design': { bg: '#fef3c7', color: '#d97706' }
                                            };
                                            const colors = productColors[product] || { bg: '#e0f2fe', color: '#0284c7' };
                                            return (
                                                <span key={idx} className="product-badge" style={{ backgroundColor: colors.bg, color: colors.color }}>
                                                    {product}
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="detail-column">
                    {selectedProperty ? (
                        <div className="card detail-card">
                            <div className="page-header-detail">
                                <a href="# " className="back-link mobile-only-back-link" onClick={(e) => { e.preventDefault(); setSelectedProperty(null); }}>←</a>
                                <h2 className="detail-title">{selectedProperty.name}</h2>
                                {canViewActions && (<div className="action-menu-container"><button className="icon-button" title="More actions" onClick={() => setOpenActionMenu(openActionMenu === selectedProperty.id ? null : selectedProperty.id)}><MoreVertical size={18} /></button>{openActionMenu === selectedProperty.id && (<div className="action-menu-dropdown">{canDownload && (<button onClick={() => { handleDownloadAll(selectedProperty.code); setOpenActionMenu(null); }}><Download size={16} /><span>Download All</span></button>)}{canManageProperties && (<><button onClick={() => { handleOpenServiceModal(selectedProperty); setOpenActionMenu(null); }}><DollarSign size={16} /><span>Add Service</span></button><button onClick={() => { handleOpenEditPropertyModal(selectedProperty); setOpenActionMenu(null); }}><Edit size={16} /><span>Edit Property</span></button></>)}</div>)}</div>)}
                            </div>
                            
                            <div className="detail-tabs">
                                <button className={`tab-button ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>Details</button>
                                <button className={`tab-button ${activeTab === 'workforce' ? 'active' : ''}`} onClick={() => setActiveTab('workforce')}>Workforce</button>
                                {canManageRooms && (
                                    <button className={`tab-button ${activeTab === 'rooms' ? 'active' : ''}`} onClick={() => setActiveTab('rooms')}>Rooms</button>
                                )}
                                <button className={`tab-button ${activeTab === 'devices' ? 'active' : ''}`} onClick={() => setActiveTab('devices')}>Devices</button>
                                {canManageSettings && (
                                    <button className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>Settings</button>
                                )}
                                {canManagePermissions && (
                                    <button className={`tab-button ${activeTab === 'permissions' ? 'active' : ''}`} onClick={() => setActiveTab('permissions')}>Permissions</button>
                                )}
                            </div>

                            <div className="tab-content">
                                {activeTab === 'details' && (
                                    <div className="property-details">
                                        {/* Status Section */}
                                        <div className="details-section status-section">
                                            <div className="status-row">
                                                <div className="status-item">
                                                    <span className={`status-badge ${selectedProperty.is_active ? 'active' : 'inactive'}`}>
                                                        {selectedProperty.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                    <span className="status-label">System</span>
                                                </div>
                                                <div className="status-divider"></div>
                                                <div className="status-item">
                                                    <span className={`status-badge status-${selectedProperty.status ? selectedProperty.status.toLowerCase().replace('_', '-') : 'pending-billing'}`}>
                                                        {selectedProperty.status ? selectedProperty.status.replace('_', ' ') : 'Pending Billing'}
                                                    </span>
                                                    <span className="status-label">Billing</span>
                                                </div>
                                                <div className="status-divider"></div>
                                                <div className="status-item">
                                                    <span className={`status-badge ${propertyHasActiveSubscriptions ? 'active' : 'inactive'}`}>
                                                        {propertyHasActiveSubscriptions ? 'Subscribed' : 'No Subscriptions'}
                                                    </span>
                                                    <span className="status-label">Service</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Property Info */}
                                        <div className="details-section">
                                            <h3 className="details-section-title">Property Information</h3>
                                            <div className="details-grid">
                                                <div className="detail-row">
                                                    <span className="detail-icon">📍</span>
                                                    <div className="detail-content">
                                                        <span className="detail-label">Property Code</span>
                                                        <span className="detail-value">{selectedProperty.code}</span>
                                                    </div>
                                                </div>
                                                <div className="detail-row">
                                                    <span className="detail-icon">🏠</span>
                                                    <div className="detail-content">
                                                        <span className="detail-label">Address</span>
                                                        <span className="detail-value">{formatAddress(selectedProperty) || 'N/A'}</span>
                                                    </div>
                                                </div>
                                                <div className="detail-row">
                                                    <span className="detail-icon">🌐</span>
                                                    <div className="detail-content">
                                                        <span className="detail-label">Website</span>
                                                        <span className="detail-value">
                                                            {selectedProperty.domain && selectedProperty.domain.trim() !== '' ? (
                                                                <a href={`http://${selectedProperty.domain}`} target="_blank" rel="noopener noreferrer">
                                                                    {selectedProperty.domain}
                                                                </a>
                                                            ) : <span className="text-muted">N/A</span>}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="detail-row">
                                                    <span className="detail-icon">✉️</span>
                                                    <div className="detail-content">
                                                        <span className="detail-label">Property Email</span>
                                                        <span className="detail-value">
                                                            {selectedProperty.email && selectedProperty.email.trim() !== '' ? (
                                                                <a href={`mailto:${selectedProperty.email}`}>{selectedProperty.email}</a>
                                                            ) : <span className="text-muted">N/A</span>}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Quick Stats */}
                                        <div className="details-section">
                                            <h3 className="details-section-title">Quick Stats</h3>
                                            <div className="stats-grid">
                                                <div className="stat-card">
                                                    <span className="stat-value">{devices.length || 0}</span>
                                                    <span className="stat-label">Devices</span>
                                                </div>
                                                <div className="stat-card">
                                                    <span className="stat-value">
                                                        {(workforceData.Owner?.length || 0) + (workforceData.Manager?.length || 0) + (workforceData.Accountant?.length || 0) + (workforceData.Housekeeper?.length || 0)}
                                                    </span>
                                                    <span className="stat-label">Team Members</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* System Info */}
                                        <div className="details-section">
                                            <h3 className="details-section-title">System Information</h3>
                                            <div className="details-grid compact">
                                                <div className="detail-row">
                                                    <span className="detail-label">Created</span>
                                                    <span className="detail-value">{formatDate(selectedProperty.created_at)}</span>
                                                </div>
                                                <div className="detail-row">
                                                    <span className="detail-label">Last Updated</span>
                                                    <span className="detail-value">{formatDate(selectedProperty.updated_at)}</span>
                                                </div>
                                                <div className="detail-row">
                                                    <span className="detail-label">Timezone</span>
                                                    <span className="detail-value">{selectedProperty.timezone || 'America/Los_Angeles'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {activeTab === 'workforce' && (isWorkforceLoading ? <p>Loading workforce...</p> : (
                                    <div className="property-group-content1">
                                        <div className="tab-content-header">
                                            <h3 className="tab-content-title">Workforce</h3>
                                            {canAddEmployee && (<button onClick={handleOpenAddEmployeeModal} className="action-button-small"><UserPlus size={16} /><span>Create User</span></button>)}
                                        </div>
                                        <RoleSection title="Owners" people={workforceData.Owner} onPersonClick={handleOpenEditEmployeeModal} currentUser={user} onImpersonate={handleInitiateImpersonation}/>
                                        <RoleSection title="Managers" people={workforceData.Manager} onPersonClick={handleOpenEditEmployeeModal} currentUser={user} onImpersonate={handleInitiateImpersonation}/>
                                        <RoleSection title="Accountants" people={workforceData.Accountant} onPersonClick={handleOpenEditEmployeeModal} currentUser={user} onImpersonate={handleInitiateImpersonation}/>
                                        
                                        {/* Housekeeper Filter Toggle */}
                                        <div className="housekeeper-filter">
                                            <button 
                                                className={`filter-pill ${housekeeperFilter === 'active' ? 'active' : ''}`}
                                                onClick={() => setHousekeeperFilter('active')}
                                            >
                                                Active ({(workforceData.Housekeeper || []).filter(p => p.is_active).length})
                                            </button>
                                            <button 
                                                className={`filter-pill ${housekeeperFilter === 'inactive' ? 'active' : ''}`}
                                                onClick={() => setHousekeeperFilter('inactive')}
                                            >
                                                Inactive ({(workforceData.Housekeeper || []).filter(p => !p.is_active).length})
                                            </button>
                                            <button 
                                                className={`filter-pill ${housekeeperFilter === 'all' ? 'active' : ''}`}
                                                onClick={() => setHousekeeperFilter('all')}
                                            >
                                                All ({(workforceData.Housekeeper || []).length})
                                            </button>
                                        </div>

                                        <RoleSection 
                                            title={housekeeperFilter === 'active' ? 'Active Housekeepers' : housekeeperFilter === 'inactive' ? 'Inactive Housekeepers' : 'All Housekeepers'}
                                            people={filteredHousekeepers} 
                                            onPersonClick={handleOpenEditEmployeeModal} 
                                            currentUser={user} 
                                            onImpersonate={handleInitiateImpersonation}
                                            dimmed={housekeeperFilter === 'inactive'}
                                            onInviteToPortal={(person) => {
                                                setInvitePortalPerson(person);
                                                setIsInvitePortalModalOpen(true);
                                            }}
                                        />
                                    </div>
                                ))}

                                {activeTab === 'rooms' && canManageRooms && (
                                    <PropertyRoomsTab property={selectedProperty} />
                                )}
                                
                                {activeTab === 'devices' && (isDevicesLoading ? <p>Loading devices...</p> : (<div className="devices-content"><div className="tab-content-header"><h3 className="tab-content-title">Devices</h3>{canManageProperties && (<button onClick={handleOpenAddDeviceModal} className="action-button-small"><PlusCircle size={16} /><span>Add Device</span></button>)}</div><div className="devices-grid">{(devices || []).map(device => (<div key={device.id} className="device-card"><span className={`status-dot ${device.is_active ? 'active' : 'inactive'}`}></span><div className="device-info"><span className="device-serial">{device.serial_number}</span><span className="device-name">{device.name || 'Unnamed Device'}</span></div><div className="device-actions">{canManageProperties && (<button className="icon-button" title="Edit Device" onClick={() => handleOpenEditDeviceModal(device)}><Edit size={18} /></button>)}<div className="action-menu-container"><button className="icon-button" title="More actions" onClick={() => setOpenDeviceActionMenu(openDeviceActionMenu === device.id ? null : device.id)}><MoreVertical size={18} /></button>{openDeviceActionMenu === device.id && (<div className="action-menu-dropdown"><button onClick={() => handleDeviceMenuAction('rebootDevice', device)}>Reboot Device</button>{canManageProperties && (<button onClick={() => handleDeviceMenuAction('setDeviceWifi', device)}>Set Wi-Fi</button>)}<button onClick={() => handleDeviceMenuAction('setDeviceTime', device)}>Set Time</button><button onClick={() => handleDeviceMenuAction('setDeviceAccessTimeZone', device)}>Set Access Time</button>{canManageProperties && (<button onClick={() => handleDeviceMenuAction('setNtpSettings', device)}>NTP Settings</button>)}
                                </div>)}</div></div></div>))}</div></div>))}
                                
                                {activeTab === 'settings' && canManageSettings && (
                                    <PropertySettingsTab 
                                        property={selectedProperty} 
                                        onUpdate={fetchProperties} 
                                    />
                                )}
                                
                                {activeTab === 'permissions' && canManagePermissions && (
                                    <PropertyPermissionsTab 
                                        property={selectedProperty} 
                                    />
                                )}
                            </div>
                        </div>
                    ) : (<div className="placeholder-text">Select a property from the list to see its details.</div>)}
                </div>
            </div>
            
            {/* Existing Modals */}
            {isPropertyModalOpen && <PropertyModal isOpen={isPropertyModalOpen} onClose={() => setIsPropertyModalOpen(false)} onSubmit={handleSaveProperty} apiError={error} properties={properties} propertyToEdit={propertyToEdit} />}
            {serviceModalState.property && <AddServiceModal isOpen={serviceModalState.isOpen} onClose={() => setServiceModalState({ isOpen: false, property: null })} property={serviceModalState.property} onSave={handleAddService} />}
            {isEditEmployeeModalOpen && selectedPerson && (<EditEmployeeModal user={user} person={selectedPerson} onClose={() => setIsEditEmployeeModalOpen(false)} onEmployeeUpdated={refreshWorkforce} propertyId={selectedProperty.id} />)}
            {isAddEmployeeModalOpen && (<AddEmployeeModal user={user} initialPropertyId={selectedProperty.id} onClose={() => setIsAddEmployeeModalOpen(false)} onEnrollmentComplete={handleEmployeeCreationComplete} onEmployeeAdded={refreshWorkforce} />)}
            {isInvitePortalModalOpen && invitePortalPerson && (
                <InviteToPortalModal 
                    person={invitePortalPerson} 
                    onClose={() => {
                        setIsInvitePortalModalOpen(false);
                        setInvitePortalPerson(null);
                    }} 
                    onInvited={refreshWorkforce} 
                />
            )}
            {isDeviceModalOpen && (<DeviceModal isOpen={isDeviceModalOpen} properties={deviceToEdit ? properties : [selectedProperty]} deviceToEdit={deviceToEdit} devices={devices} onClose={() => { setIsDeviceModalOpen(false); setDeviceToEdit(null); }} onSubmit={handleSaveDevice} initialPropertyId={selectedProperty.id} />)}
            {deviceActionState.type === 'rebootDevice' && (<RebootModal device={deviceActionState.device} onClose={closeDeviceActionModal} isSubmitting={isSubmittingDeviceAction} onConfirm={() => handleDeviceAction('rebootDevice', deviceActionState.device.id)} />)}
            {deviceActionState.type === 'setDeviceWifi' && (<WiFiConfigModal device={deviceActionState.device} onClose={closeDeviceActionModal} isSubmitting={isSubmittingDeviceAction} onSubmit={(data) => handleDeviceAction('setDeviceWifi', deviceActionState.device.id, data)} />)}
            {deviceActionState.type === 'setDeviceTime' && (<SetTimeModal device={deviceActionState.device} onClose={closeDeviceActionModal} isSubmitting={isSubmittingDeviceAction} onSubmit={(data) => handleDeviceAction('setDeviceTime', deviceActionState.device.id, data)} />)}
            {deviceActionState.type === 'setDeviceAccessTimeZone' && (<AccessTimeModal device={deviceActionState.device} onClose={closeDeviceActionModal} isSubmitting={isSubmittingDeviceAction} onSubmit={(data) => handleDeviceAction('setDeviceAccessTimeZone', deviceActionState.device.id, data)} />)}
            {deviceActionState.type === 'setNtpSettings' && (<NtpSettingsModal device={deviceActionState.device} onClose={closeDeviceActionModal} isSubmitting={isSubmittingDeviceAction} onSubmit={(data) => handleDeviceAction('setNtpSettings', deviceActionState.device.id, data)} />)}

            {impersonateTarget && (
                <div className="modal-backdrop">
                    <div className="modal-content" style={{ maxWidth: '450px' }}>
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ 
                                width: '50px', height: '50px', borderRadius: '50%', 
                                background: '#eff6ff', color: '#1798c1', 
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 15px auto'
                            }}>
                                <LogIn size={28} />
                            </div>
                            <h2 style={{ textAlign: 'center', marginBottom: '10px' }}>Login as {impersonateTarget.first_name}?</h2>
                            <p style={{ textAlign: 'center', color: '#64748b', lineHeight: '1.5' }}>
                                You are about to access the system as <strong>{impersonateTarget.first_name} {impersonateTarget.last_name}</strong>. 
                                <br/><br/>
                                You will see exactly what they see. Your Admin session will be paused.
                            </p>
                        </div>
                        <div className="modal-actions" style={{ justifyContent: 'center', gap: '15px' }}>
                            <button 
                                className="button button-secondary" 
                                onClick={() => setImpersonateTarget(null)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="button action-button" 
                                onClick={executeImpersonation}
                                style={{ backgroundColor: '#0f172a' }} // Dark button
                            >
                                Yes, Login as User
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropertiesScreen;